import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import type { ReactText } from 'react';
import { useSelector } from 'react-redux';
import { interval } from 'rxjs';

import { TimedStatus, useCasinoBonusGame } from '@swf/casino';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import { GADimension, Size } from '@swf/core/enums';
import useScreenSize from '@swf/core/hooks/useScreenSize';
import useTrackers from '@swf/core/hooks/useTrackers';
import { select } from '@swf/core/store';
import { BonusGameTypeVariant, BonusStatus } from '@swf/core/types/CasinoBonus';
import type { SpinOfDayData } from '@swf/core/types/CasinoBonus';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';

import Clock from '../../../../assets/clock.svg';

import {
  TXTCLI_Play,
  TXTWCL_1_Spin,
  TXTWCL_N0_Spins,
  TXTWCL_XtitleXExpires_inXX_titleXXsub_ELL
} from './Ticket.messages.json';
import styles from './Ticket.scss';

interface Props {
  data: SpinOfDayData;
  layout: number;
}

const Ticket: React.FC<Props> = ({ data, layout }) => {
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const useButton = !isMobile && greaterThan(Size.Small);
  const { status, numberOfSpins, expiryTime, theme, themeId, variantType } = data;
  const isAvailable = status === BonusStatus.Available;
  const [startTime, setStartTime] = useState<number>(Date.now());
  const trackingDimensions = {
    [GADimension.TabName]: 'Spin of the Day',
    [GADimension.CampaignID]: data.bonusCampaignId,
    [GADimension.TicketID]: data.bonusId
  };
  const spinStyle = /mega/i.test(themeId) ? styles.iconTitleText : styles.iconTitleTextSpin;

  const launchGame = useCasinoBonusGame();
  const { buttonPressed } = useTrackers();

  const message = () => {
    if (variantType === BonusGameTypeVariant.BonusRound) {
      return '<spin>BONUS ROUNDS</spin>';
    } else {
      return locale === 'ro'
        ? '<spin>Rotirea</spin> <day>Zilei</day>'
        : '<spin>Spin</spin> <secondary>of</secondary> <secondary>the</secondary> <day>Day</day>';
    }
  };

  const locale = useSelector(select.locale.get);
  const tileTitle: TrackedTranslation = {
    id: 'ID_DOES_NOT_EXIST',
    defaultMessage: message(),
    flags: ['HTML', 'TRACK'],
    track: 'Spin of the Day'
  };

  const launch = () => {
    if (data) {
      launchGame(data);
      isMobile && buttonPressed({ buttonName: 'Play' }, trackingDimensions);
    }
  };

  useEffect(() => {
    const base = interval(1000).subscribe(() => setStartTime(Date.now()));
    return () => base.unsubscribe();
  }, []);

  return (
    <div
      className={classNames(styles['layout' + layout], styles.container)}
      onClick={!useButton && isAvailable ? launch : undefined}
    >
      <div className={styles.iconContainer} style={getBackgroundImageStyle(theme)}>
        <div className={styles.iconTile}>
          <Translation
            {...tileTitle}
            values={{
              spin: (text: ReactText) => <span className={spinStyle}>{text}</span>,
              day: (text: ReactText) => <span className={styles.iconTitleText}>{text}</span>,
              secondary: (text: ReactText) => <span className={styles.iconTitleText}>{text}</span>
            }}
          />
        </div>
      </div>
      <div className={styles.infoContainer}>
        <div className={styles.spinInfo}>
          <Translation
            {...(numberOfSpins > 1 ? TXTWCL_N0_Spins : TXTWCL_1_Spin)}
            values={{ 0: numberOfSpins }}
          />
        </div>
        <div className={styles.expiryInfo}>
          <Translation
            {...TXTWCL_XtitleXExpires_inXX_titleXXsub_ELL}
            values={{
              0: (
                <TimedStatus running startTime={startTime} endTime={expiryTime} count={startTime} />
              ),
              title: (text: ReactText) => <span>{text}</span>,
              subtitle: (text: ReactText) => (
                <span className={styles.number}>
                  <Clock />
                  {text}
                </span>
              )
            }}
          />
        </div>
        {useButton && (
          <StarsButton
            onClick={launch}
            disabled={!isAvailable}
            size="small"
            className={styles.playButton}
            translation={TXTCLI_Play}
            trackingDimensions={trackingDimensions}
          />
        )}
      </div>
    </div>
  );
};

export default Ticket;
